import { importProvidersFrom } from '@angular/core';
import { BrowserModule, bootstrapApplication } from '@angular/platform-browser';
import { withInterceptorsFromDi, provideHttpClient } from '@angular/common/http';
import { provideAnimations, provideNoopAnimations } from '@angular/platform-browser/animations';
import { ServiceWorkerModule } from '@angular/service-worker';
import { TitleCasePipe, DatePipe, DecimalPipe } from '@angular/common';

import { MatLegacySnackBarModule as MatSnackBarModule } from '@angular/material/legacy-snack-bar';
import { MatLegacyDialogModule as MatDialogModule } from '@angular/material/legacy-dialog';
import { MomentDateAdapter, MAT_MOMENT_DATE_ADAPTER_OPTIONS } from '@angular/material-moment-adapter';
import { MAT_DATE_LOCALE, DateAdapter, MAT_DATE_FORMATS } from '@angular/material/core';
import {
  MAT_LEGACY_TOOLTIP_DEFAULT_OPTIONS as MAT_TOOLTIP_DEFAULT_OPTIONS,
  MatLegacyTooltipDefaultOptions as MatTooltipDefaultOptions
} from '@angular/material/legacy-tooltip';

import { IPublicClientApplication, PublicClientApplication } from '@azure/msal-browser';
import { MSAL_INSTANCE, MsalService, MsalBroadcastService, MsalModule } from '@azure/msal-angular';
import { GridApi, LicenseManager } from 'ag-grid-enterprise';

import { NgxEchartsModule } from 'ngx-echarts';
import { provideNgxMask, NgxMaskDirective, NgxMaskPipe } from 'ngx-mask';
import { Options } from '@angular-slider/ngx-slider';

import { AppComponent } from './app/app.component';
import { AppRoutingModule } from './app/app-routing.module';
import { InterceptorModule } from './app/interceptors/interceptor.module';
import { UsersService } from './app/services/users.service';
import { ObservableService } from './app/services/observable.service';
import { MenuShortCutService } from '@services/menu-short-cut.service';
import { UserBehaviorService } from '@services/user-events/user-behavior.service';
import { LocalStorageService } from './app/connectors/localstorage';

import { environment } from './environments/environment';

const isIE = window.navigator.userAgent.indexOf('MSIE ') > -1 || window.navigator.userAgent.indexOf('Trident/') > -1;

LicenseManager.setLicenseKey(
  'Using_this_{AG_Grid}_Enterprise_key_{AG-101435}_in_excess_of_the_licence_granted_is_not_permitted___Please_report_misuse_to_legal@ag-grid.com___For_help_with_changing_this_key_please_contact_info@ag-grid.com___{NTT_Data}_is_granted_a_{Single_Application}_Developer_License_for_the_application_{opsmgmtp}_only_for_{6}_Front-End_JavaScript_developers___All_Front-End_JavaScript_developers_working_on_{opsmgmtp}_need_to_be_licensed___{opsmgmtp}_has_not_been_granted_a_Deployment_License_Add-on___This_key_works_with_{AG_Grid}_Enterprise_versions_released_before_{21_March_2027}____[v3]_[01]_MTgwNTU4NzIwMDAwMA==b89a7386acbd811f9c32101332f8dc30'
);

export function MSALInstanceFactory(): IPublicClientApplication {
  return new PublicClientApplication({
    auth: {
      clientId: '9a1a11dc-b5c1-469c-8b9a-3850ea5e38d7',
      authority: 'https://login.microsoftonline.com/e3cf3c98-a978-465f-8254-9d541eeea73c',
      redirectUri: environment.redirectUri,
      postLogoutRedirectUri: environment.redirectUri
    },
    cache: {
      cacheLocation: 'localStorage',
      storeAuthStateInCookie: isIE,
      secureCookies: true
    }
  });
}

export const customTooltip: MatTooltipDefaultOptions = {
  showDelay: 0,
  hideDelay: 0,
  touchendHideDelay: 1500,
  disableTooltipInteractivity: true
};

export const MY_FORMATS = {
  parse: {
    dateInput: 'MM/DD/YYYY'
  },
  display: {
    dateInput: 'MM/DD/YYYY',
    monthYearLabel: 'MMM YYYY',
    dateA11yLabel: 'MM/DD/YYYY',
    monthYearA11yLabel: 'MMMM YYYY'
  }
};

bootstrapApplication(AppComponent, {
  providers: [
    importProvidersFrom(
      BrowserModule,
      AppRoutingModule,
      MatDialogModule,
      InterceptorModule,
      MsalModule,
      MatSnackBarModule,
      NgxEchartsModule.forRoot({
        echarts: () => import('echarts')
      }),
      ServiceWorkerModule.register('ngsw-worker.js', {
        enabled: true,
        registrationStrategy: 'registerWhenStable:30000'
      }),
      NgxMaskDirective,
      NgxMaskPipe
    ),
    { provide: MAT_DATE_LOCALE, useValue: 'en-US' },
    {
      provide: DateAdapter,
      useClass: MomentDateAdapter,
      deps: [MAT_DATE_LOCALE]
    },
    { provide: MAT_DATE_FORMATS, useValue: MY_FORMATS },
    { provide: MAT_MOMENT_DATE_ADAPTER_OPTIONS, useValue: { useUtc: true } },
    { provide: MSAL_INSTANCE, useFactory: MSALInstanceFactory },
    { provide: MAT_TOOLTIP_DEFAULT_OPTIONS, useValue: customTooltip },
    MsalService,
    MsalBroadcastService,
    LocalStorageService,
    UserBehaviorService,
    TitleCasePipe,
    Options,
    GridApi,
    MenuShortCutService,
    DatePipe,
    DecimalPipe,
    ObservableService,
    UsersService,
    provideNgxMask(),
    provideHttpClient(withInterceptorsFromDi()),
    provideAnimations(),
    provideNoopAnimations()
  ]
}).catch((err) => console.error(err));
